import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24 md:pt-20 px-4 sm:px-6">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/3dca1a865400ab72929c3fdc6e7bf329.jpg"
          alt="Instalación profesional de pladur"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-dark-bg"></div>
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-orange-primary rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.5, 0.2],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto py-20">
        <div className="max-w-4xl">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center space-x-2 px-4 py-2 border border-orange-primary rounded-full mb-8"
          >
            <i className="ri-map-pin-line text-orange-primary text-sm"></i>
            <span className="text-text-secondary text-xs sm:text-sm font-medium">
              Instaladores profesionales en Badalona
            </span>
          </motion.div>

          {/* H1 */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-text-primary mb-6 leading-tight break-words"
          >
            Instalación de pladur
            <br />
            <span className="bg-gradient-to-r from-orange-primary to-orange-secondary bg-clip-text text-transparent">
              en Badalona
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-base sm:text-lg md:text-xl text-text-secondary max-w-2xl mb-10 leading-relaxed"
          >
            Obra rápida, limpia y acabados profesionales. Tabiques, trasdosados, falsos techos y aislamiento para tu hogar u oficina. Ofrecemos <a href="https://hospitalet.pladures.es/" target="_blank" rel="noopener noreferrer" className="text-orange-primary hover:underline">servicios profesionales de instalación de pladur</a> en toda la zona.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 mb-12"
          >
            <Link
              to="/contacto"
              className="px-6 sm:px-8 py-3 sm:py-4 bg-orange-primary text-white rounded-full hover:shadow-glow-orange transition-all duration-300 text-sm sm:text-base font-bold whitespace-nowrap inline-flex items-center justify-center space-x-2"
            >
              <span>Solicitar presupuesto</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/servicios"
              className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-white text-white rounded-full hover:bg-white hover:text-dark-bg transition-all duration-300 text-sm sm:text-base font-bold whitespace-nowrap inline-flex items-center justify-center"
            >
              Ver servicios
            </Link>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="grid grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8"
          >
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start space-x-2 mb-2">
                <i className="ri-checkbox-circle-line text-orange-primary text-xl sm:text-2xl"></i>
                <span className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-text-primary">500+</span>
              </div>
              <p className="text-text-muted text-xs sm:text-sm">Proyectos completados</p>
            </div>
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start space-x-2 mb-2">
                <i className="ri-time-line text-orange-primary text-xl sm:text-2xl"></i>
                <span className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-text-primary">24h</span>
              </div>
              <p className="text-text-muted text-xs sm:text-sm">Respuesta rápida</p>
            </div>
            <div className="text-center md:text-left col-span-2 md:col-span-1">
              <div className="flex items-center justify-center md:justify-start space-x-2 mb-2">
                <i className="ri-map-pin-2-line text-orange-primary text-xl sm:text-2xl"></i>
                <span className="text-xl sm:text-2xl md:text-3xl font-extrabold text-text-primary">Badalona</span>
              </div>
              <p className="text-text-muted text-xs sm:text-sm">y Barcelona</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <i className="ri-arrow-down-line text-orange-primary text-2xl sm:text-3xl"></i>
      </motion.div>
    </section>
  );
};

export default HeroSection;
